import type { Cart, CartItem } from 'utils/types/Cart';
import type { Beer } from 'utils/types/Beer';

// Configurações do localStorage
const STORAGE_KEY = 'clube42-cart';
const MAX_AGE_DAYS = 7;
const STORAGE_VERSION = '1.0';

interface StoredCart extends Cart {
  version: string;
  lastUpdated: string;
}

// interface StoredCartItem extends Omit<CartItem, 'addedAt'> {
//   addedAt: string; // ISO string para serialização
// }

/**
 * Utilitários para persistência do carrinho no localStorage
 */
export class CartStorage {
  /**
   * Salva o carrinho no localStorage
   */
  static saveCart(cart: Cart): void {
    try {
      const storedCart: StoredCart = {
        ...cart,
        version: STORAGE_VERSION,
        lastUpdated: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedCart));
      // console.log('[CartStorage] Carrinho salvo com sucesso', {
      //   totalItems: cart.totalItems,
      // });
    } catch (error) {
      console.error('[CartStorage] Erro ao salvar carrinho:', error);
    }
  }

  /**
   * Carrega o carrinho do localStorage
   */
  static loadCart(): Cart | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // console.log('[CartStorage] Nenhum carrinho salvo encontrado');
        return null;
      }

      const parsedCart: StoredCart = JSON.parse(stored);

      // Verificar versão
      if (parsedCart.version !== STORAGE_VERSION) {
        // console.log('[CartStorage] Versão incompatível, limpando carrinho');
        CartStorage.clearCart();
        return null;
      }

      // Verificar idade dos dados
      const lastUpdated = new Date(parsedCart.lastUpdated);
      const maxAge = new Date();
      maxAge.setDate(maxAge.getDate() - MAX_AGE_DAYS);

      if (lastUpdated < maxAge) {
        // console.log('[CartStorage] Carrinho muito antigo, limpando');
        CartStorage.clearCart();
        return null;
      }

      // Validar e limpar itens expirados
      const validItems = CartStorage.validateAndCleanItems(parsedCart.items);

      if (validItems.length !== parsedCart.items.length) {
        // console.log(
        //   '[CartStorage] Alguns itens foram removidos por serem inválidos',
        // );
      }

      // Recalcular totais
      const cart = CartStorage.recalculateTotals({
        ...parsedCart,
        items: validItems,
      });

      // console.log('[CartStorage] Carrinho carregado com sucesso', {
      //   totalItems: cart.totalItems,
      //   validItems: validItems.length,
      // });

      return cart;
    } catch (error) {
      // console.error('[CartStorage] Erro ao carregar carrinho:', error);
      CartStorage.clearCart();
      return null;
    }
  }

  /**
   * Remove o carrinho do localStorage
   */
  static clearCart(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      // console.log('[CartStorage] Carrinho removido do localStorage');
    } catch (error) {
      // console.error('[CartStorage] Erro ao limpar carrinho:', error);
    }
  }

  /**
   * Valida e limpa itens do carrinho
   */
  private static validateAndCleanItems(items: CartItem[]): CartItem[] {
    const maxItemAge = new Date();
    maxItemAge.setDate(maxItemAge.getDate() - MAX_AGE_DAYS);

    return items.filter(item => {
      // Verificar se o item tem dados válidos
      if (
        !item.beer ||
        !item.beer.id ||
        !item.beer.name ||
        item.quantity <= 0
      ) {
        console.warn('[CartStorage] Item inválido removido:', item);
        return false;
      }

      // Verificar idade do item
      const addedAt = new Date(item.addedAt);
      if (addedAt < maxItemAge) {
        // console.log(
        //   '[CartStorage] Item muito antigo removido:',
        //   item.beer.name,
        // );
        return false;
      }

      // Verificar se ainda está em estoque (assumindo que todos estão, mas pode ser expandido)
      if (!item.beer.inStock) {
        // console.log(
        //   '[CartStorage] Item fora de estoque removido:',
        //   item.beer.name,
        // );
        return false;
      }

      return true;
    });
  }

  /**
   * Recalcula os totais do carrinho
   */
  private static recalculateTotals(
    cart: Omit<Cart, 'totalItems' | 'totalValue' | 'subscriberTotalValue'>,
  ): Cart {
    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const totalValue =
      Math.round(
        cart.items.reduce(
          (sum, item) => sum + item.beer.price * item.quantity,
          0,
        ) * 100,
      ) / 100;
    const subscriberTotalValue =
      Math.round(
        cart.items.reduce(
          (sum, item) => sum + item.beer.subscriberPrice * item.quantity,
          0,
        ) * 100,
      ) / 100;

    return {
      ...cart,
      totalItems,
      totalValue,
      subscriberTotalValue,
    };
  }

  /**
   * Verifica se o localStorage está disponível
   */
  static isStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Obtém informações sobre o uso do localStorage
   */
  static getStorageInfo(): { size: number; available: boolean } {
    try {
      const cart = localStorage.getItem(STORAGE_KEY);
      const size = cart ? new Blob([cart]).size : 0;

      return {
        size,
        available: CartStorage.isStorageAvailable(),
      };
    } catch {
      return {
        size: 0,
        available: false,
      };
    }
  }

  /**
   * Valida se um produto ainda é válido (pode ser expandido com verificações de API)
   */
  static validateBeer(beer: Beer): boolean {
    // Validações básicas
    if (
      !beer.id ||
      !beer.name ||
      !beer.brewery ||
      !beer.price ||
      !beer.subscriberPrice
    ) {
      return false;
    }

    // Verificar se os preços são válidos
    if (
      beer.price <= 0 ||
      beer.subscriberPrice <= 0 ||
      beer.subscriberPrice > beer.price
    ) {
      return false;
    }

    // Aqui poderia ser adicionada uma verificação com API para ver se o produto ainda existe
    // Por enquanto, assumimos que está válido se passou nas validações básicas
    return true;
  }

  /**
   * Migra dados de versões antigas (futuro)
   */
  static migrateOldData(): void {
    // Função para migrar dados de versões antigas do carrinho
    // Por enquanto, apenas limpa dados incompatíveis
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (!parsed.version || parsed.version !== STORAGE_VERSION) {
          // console.log('[CartStorage] Migrando dados antigos...');
          CartStorage.clearCart();
        }
      } catch {
        CartStorage.clearCart();
      }
    }
  }
}
